// CN-1323: a managed dependent (a loginless minor) can't be billed directly, so
// a family member's session/registration is paid by the family head. These
// helpers resolve the fixed payer when every selected athlete belongs to one
// family, so the booking and program-registration flows can lock the payer and
// surface a "billed to the parent / guardian" note.

// When every selected athlete belongs to ONE family — each is either a dependent
// of the same head, or the head themself — the payer is unambiguously that head.
// Map each athlete to its "effective payer id" (a dependent → its headExternalId,
// anyone else → its own id, which is also an external id); if the set collapses
// to a single id AND that head is one of the selected athletes, return that head.
// Otherwise null (mixed families, unrelated athletes, or a head who isn't a
// participant), so callers fall back to their editable / per-athlete behavior.
export function soleFamilyHead(selectedAthletes: any[]): any | null {
  if (!selectedAthletes || selectedAthletes.length < 2) {
    return null;
  }

  const payerIds = new Set(selectedAthletes.map((a) => a?.headExternalId || a?.id));
  if (payerIds.size !== 1) {
    return null;
  }

  const [headId] = [...payerIds];
  if (!headId) {
    return null;
  }

  return selectedAthletes.find((a) => a.id === headId) ?? null;
}

// The fixed family payer for a selection: a one-family group resolves to its
// head, a lone dependent to its (serialized) head. `lockedPayer` is the profile
// to bill (null when the payer is ambiguous); `payerName` is the head's display
// name (null when only a fallback label applies — the caller supplies it).
export function resolveFamilyPayer(
  selectedAthletes: any[],
): { lockedPayer: any | null; payerName: string | null } {
  const soleDependent =
    selectedAthletes?.length === 1 && selectedAthletes[0]?.headExternalId
      ? selectedAthletes[0]
      : null;
  const familyHead = soleFamilyHead(selectedAthletes);
  const lockedPayer = familyHead ?? soleDependent;
  const payerName = familyHead?.fullName || soleDependent?.headFullName || null;

  return { lockedPayer, payerName };
}

// A family group within a multi-athlete selection: members that share one payer
// (a head and/or their managed dependents), kept contiguous so a list can show a
// single "billed to parent / guardian" note per family — even when the selection
// also contains other families or unrelated athletes (CFTB-3321). `isFamily` is
// true when the group contains a dependent (→ show the note); `payerName` is the
// head's display name when resolvable.
export type FamilyPayerGroup = {
  key: string;
  members: any[];
  isFamily: boolean;
  payerName: string | null;
};

// Group a selection by family payer, preserving each group's first-appearance
// order and each member's order within it. A member's key is its headExternalId
// (a dependent) or its own id (a head or a standalone athlete), so a head picked
// alongside their dependents lands in the same group. Unlike resolveFamilyPayer
// (which only resolves a payer when the WHOLE selection is one family), this
// resolves a payer per group, so mixed selections keep a note for each family.
export function groupFamilyPayers(selectedAthletes: any[]): FamilyPayerGroup[] {
  const groups: FamilyPayerGroup[] = [];
  const indexByKey = new Map<string, number>();

  (selectedAthletes ?? []).forEach((athlete) => {
    const key = athlete?.headExternalId || athlete?.id;
    if (!key) {
      return;
    }

    let index = indexByKey.get(key);
    if (index === undefined) {
      index = groups.length;
      indexByKey.set(key, index);
      groups.push({ key, members: [], isFamily: false, payerName: null });
    }

    groups[index].members.push(athlete);
  });

  groups.forEach((group) => {
    const dependent = group.members.find((member) => member.headExternalId);
    if (!dependent) {
      return;
    }

    const head = group.members.find((member) => member.id === group.key);
    group.isFamily = true;
    group.payerName = head?.fullName || dependent.headFullName || null;
  });

  return groups;
}
